import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";

import ErrorBoundary from "./components/ErrorBoundary";

// Prevent ResizeObserver callback re-entrancy loops from rich editors.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

// Patch Node.prototype.removeChild and Node.prototype.insertBefore to protect React
// from crashing when browser translation extensions (e.g. Google Translate) or third-party
// scripts (e.g. Microsoft Clarity, Grammarly) wrap text nodes in <font> tags.
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      if (console && typeof console.warn === "function") {
        console.warn(
          "[DOM Guard] Node to remove is not a direct child of this node. Bypassing or removing from actual parent to protect React reconciliation.",
          child
        );
      }
      if (child.parentNode) {
        return child.parentNode.removeChild(child);
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console && typeof console.warn === "function") {
        console.warn(
          "[DOM Guard] Reference node is not a direct child of this node. Appending to parent instead to protect React reconciliation.",
          referenceNode
        );
      }
      if (referenceNode.parentNode) {
        return referenceNode.parentNode.insertBefore(newNode, referenceNode);
      }
      return this.appendChild(newNode);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

const originalConsoleError = console.error;
console.error = (...args) => {
  const first = args?.[0];
  const message = typeof first === "string" ? first : first?.message || "";
  if (
    message.includes("ResizeObserver loop completed with undelivered notifications") ||
    message.includes("ResizeObserver loop limit exceeded") ||
    message.includes("The node to be removed is not a child of this node")
  ) {
    return;
  }
  originalConsoleError(...args);
};

// Suppress known benign ResizeObserver loop noise and external translation removeChild noise.
function suppressBenignRuntimeErrors(event) {
  const message = event?.message || event?.reason?.message || "";
  if (
    message.includes("ResizeObserver loop completed with undelivered notifications") ||
    message.includes("ResizeObserver loop limit exceeded") ||
    message.includes("The node to be removed is not a child of this node")
  ) {
    event.preventDefault();
    event.stopImmediatePropagation?.();
  }
}

window.addEventListener("error", suppressBenignRuntimeErrors, true);
window.addEventListener("unhandledrejection", suppressBenignRuntimeErrors, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();
